import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  deleteUser,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile as updateFirebaseProfile,
  type User as FirebaseUser,
} from "firebase/auth";
import { deleteDoc, doc, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "@/firebase/config";
import type { AppUser } from "@/types/user";

/**
 * AuthenticationService — única porta de entrada para login, cadastro,
 * recuperação de senha e leitura/escrita do perfil do usuário.
 * Componentes React nunca chamam Firebase Auth diretamente: sempre por aqui.
 */

function mapFirebaseUser(user: FirebaseUser): Pick<AppUser, "uid" | "nome" | "email" | "foto"> {
  return {
    uid: user.uid,
    nome: user.displayName ?? "",
    email: user.email ?? "",
    foto: user.photoURL,
  };
}

async function ensureUserDocument(user: FirebaseUser): Promise<void> {
  const ref = doc(db, "users", user.uid);
  const snapshot = await getDoc(ref);

  if (!snapshot.exists()) {
    const newUser: Omit<AppUser, "uid"> = {
      nome: user.displayName ?? "",
      email: user.email ?? "",
      foto: user.photoURL,
      dataCadastro: new Date().toISOString(),
      ultimoLogin: new Date().toISOString(),
      ativo: true,
      premium: false,
      idioma: "pt-BR",
      tema: "system",
      gruposParticipando: [],
      gruposAdministrando: [],
    };
    await setDoc(ref, { ...newUser, dataCadastro: serverTimestamp(), ultimoLogin: serverTimestamp() });
  } else {
    await updateDoc(ref, { ultimoLogin: serverTimestamp() });
  }
}

export const AuthenticationService = {
  /** Observa mudanças de sessão. Usado pelo AuthContext. */
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  },

  async signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    const credential = await signInWithPopup(auth, provider);
    await ensureUserDocument(credential.user);
    return mapFirebaseUser(credential.user);
  },

  async signInWithEmail(email: string, password: string) {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    await ensureUserDocument(credential.user);
    return mapFirebaseUser(credential.user);
  },

  async registerWithEmail(name: string, email: string, password: string) {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    await updateFirebaseProfile(credential.user, { displayName: name });
    await ensureUserDocument(credential.user);
    return mapFirebaseUser(credential.user);
  },

  async sendPasswordReset(email: string) {
    await sendPasswordResetEmail(auth, email);
  },

  async signOut() {
    await signOut(auth);
  },

  async getUserProfile(uid: string): Promise<AppUser | null> {
    const ref = doc(db, "users", uid);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) return null;
    return { uid, ...(snapshot.data() as Omit<AppUser, "uid">) };
  },

  async updateUserProfile(uid: string, data: Partial<Pick<AppUser, "nome" | "foto" | "cidade" | "estado" | "telefone" | "tema" | "idioma">>) {
    const ref = doc(db, "users", uid);
    await updateDoc(ref, data);

    if (data.nome || data.foto) {
      const current = auth.currentUser;
      if (current) {
        await updateFirebaseProfile(current, {
          displayName: data.nome,
          photoURL: data.foto ?? undefined,
        });
      }
    }
  },

  /** Método de login do usuário atual — usado pra saber como reautenticar antes de excluir a conta. */
  getCurrentSignInMethod(): "google.com" | "password" | null {
    const providerId = auth.currentUser?.providerData[0]?.providerId;
    if (providerId === "google.com") return "google.com";
    if (providerId === "password") return "password";
    return null;
  },

  /**
   * Reautentica o usuário logado — obrigatório pelo Firebase Auth antes de
   * excluir a conta se o login foi feito há um tempo (erro
   * `auth/requires-recent-login`). `password` só é necessário pra contas de
   * e-mail/senha; contas Google reabrem o popup do Google.
   */
  async reauthenticate(password?: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error("Você precisa estar autenticado.");

    const method = this.getCurrentSignInMethod();
    if (method === "google.com") {
      await reauthenticateWithPopup(user, new GoogleAuthProvider());
    } else if (method === "password") {
      if (!user.email || !password) throw new Error("Informe sua senha para confirmar.");
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
    } else {
      throw new Error("Método de login não suportado para reautenticação.");
    }
  },

  /**
   * Exclusão de conta (Priority 15 do escopo "bolão funcional de verdade").
   * Apaga o documento pessoal em `users/{uid}` e a conta de autenticação.
   *
   * IMPORTANTE — limitação conhecida: isto NÃO faz cascata em dados
   * relacionados (participação em ligas, palpites salvos, etc.) — remover
   * o usuário de `groups.memberUids`/`participants` de cada liga que ele
   * participa exigiria uma Cloud Function (o client não tem permissão nem
   * visibilidade pra alterar ligas de outros administradores). Isso fica
   * para uma etapa própria de "exclusão de conta com cascata via Cloud
   * Function" — aqui a conta e os dados estritamente pessoais são
   * realmente excluídos, o que já cumpre o pedido central desta etapa.
   */
  async deleteAccount(password?: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error("Você precisa estar autenticado.");

    try {
      await deleteDoc(doc(db, "users", user.uid));
    } catch {
      // Não impede a exclusão da conta de autenticação em si se o
      // documento do Firestore já não existir ou falhar por outro motivo.
    }

    try {
      await deleteUser(user);
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === "auth/requires-recent-login") {
        await this.reauthenticate(password);
        await deleteUser(user);
      } else {
        throw err;
      }
    }
  },
};
